/**
 * "What does this query actually select?" — shown beside a dataset's query
 * box.
 *
 * A dataset carries two queries that look alike and mean different things:
 * the deliverables query picks the items that get forecast, the work-items
 * query picks the broader set counted as their unfinished work. Labels and
 * help text have not made that difference land. Running each query and
 * showing the count and the first few keys does, because the user recognises
 * the items.
 *
 * Two deliberate constraints:
 *
 * - **A button, never per keystroke.** Each preview is a full key listing
 *   against the tracker, and a half-typed JQL is not a question worth
 *   asking. The cost is visible and the user chooses to pay it.
 * - **A stale result is withdrawn, not shown.** Once the query text differs
 *   from the text that produced the result, the result is about a query that
 *   no longer exists — "42 matches" beside an edited query is worse than no
 *   answer at all.
 */

import { useState } from 'react';
import { Button } from '@lean-agility/design-system/react';

import { ApiError } from '@/lib/api';
import { useQueryPreview } from '../hooks';
import type { QueryPreviewResponse } from '../types';

/** A preview result together with the exact query text that produced it. */
type Snapshot = { query: string; result: QueryPreviewResponse };

export function QueryPreviewPanel({
  sourceId,
  query,
  label,
  disabled,
}: {
  /** `null` until a source is chosen — there is nothing to run against. */
  sourceId: string | null;
  /** Current text of the query box, live. */
  query: string;
  /** What this query is, for the button's accessible name. */
  label: string;
  disabled: boolean;
}) {
  const preview = useQueryPreview();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);

  const trimmed = query.trim();
  const runnable = !!sourceId && trimmed.length > 0 && !disabled && !preview.isPending;

  // Derived, not an effect: comparing against the snapshot's own query is
  // what makes an edited box drop its stale answer, with no state to
  // synchronise and no render that shows the wrong number.
  const errorFor =
    preview.isError && preview.variables?.query === trimmed
      ? preview.error instanceof ApiError
        ? preview.error.message
        : 'Preview failed.'
      : null;
  // An error about *this* query text supersedes any earlier success for the
  // same text — re-previewing an unchanged query against a tracker that has
  // since gone down would otherwise render the old count beside the failure,
  // and the count is the more believable of the two.
  const current = !errorFor && snapshot && snapshot.query === trimmed ? snapshot.result : null;

  const run = () => {
    if (!sourceId || !runnable) return;
    preview.mutate({ sourceId, query: trimmed }, { onSuccess: (result) => setSnapshot({ query: trimmed, result }) });
  };

  return (
    <div className="mt-2">
      <Button type="button" variant="secondary" size="sm" onClick={run} disabled={!runnable}>
        {preview.isPending ? 'Previewing…' : `Preview ${label}`}
      </Button>

      {/* Announced politely: the result arrives after an explicit action, so
          a screen-reader user needs to hear it without losing their place. */}
      <div aria-live="polite" className="mt-2 text-sm">
        {errorFor && (
          <p className="text-feedback-error">
            {/* The tracker's own diagnostic, verbatim — it names the field to
                fix, which no message of ours could. */}
            {errorFor}
          </p>
        )}

        {current && current.count === 0 && (
          <p className="text-text-secondary">
            Matches no items. The query is valid — it just selects nothing right now.
          </p>
        )}

        {current && current.count > 0 && (
          <>
            <p className="text-text-secondary">
              Matches {current.count} {current.count === 1 ? 'item' : 'items'}
              {current.sample.length < current.count && `, first ${current.sample.length} shown`}:
            </p>
            <ul className="mt-1 space-y-0.5">
              {current.sample.map((item) => (
                <li key={item.key} className="flex flex-wrap items-baseline gap-x-2">
                  <code className="text-xs font-medium">{item.key}</code>
                  <span className="text-text-secondary">{item.summary ?? '(no summary)'}</span>
                  {item.issue_type && <span className="text-xs text-text-secondary">· {item.issue_type}</span>}
                  {item.status && <span className="text-xs text-text-secondary">· {item.status}</span>}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
